import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { get } from "lodash";
import ServiceProvider from "@client.services/provider";
import { getError } from "@client.utils/errors";

const initialState = {
  profile: null,
  error: null,
  loading: false,
  loadingProfile: false,
  isCostIndexPresentation: true,
  lastViewedInsightsAt: null,
  lastInsightsUpdateAt: null,
  syncLoading: false,
  isDemo: false,
};

export const processLoginAsync = createAsyncThunk(
  "login/login",
  async ({ token, email, isGoogle }, thunkAPI) => {
    try {
      await ServiceProvider.Log.log("Login process started", {
        email,
        isGoogle,
      });

      const profile = await ServiceProvider.Auth.login(email, token, isGoogle);
      if (!profile) {
        await ServiceProvider.Log.log("Login failed: No profile returned", {
          email,
        });
        return null;
      }

      await ServiceProvider.Log.log("Login successful", {
        email,
        userId: profile.id,
      });

      // Skip calendar sync for demo users
      if (!profile.isDemo) {
        await thunkAPI.dispatch(syncCalendarEventsAsync(isGoogle));

        await ServiceProvider.Log.log("Calendar sync initiated", {
          email,
          isGoogle,
        });
      } else {
        await ServiceProvider.Log.log("Calendar sync skipped for demo user", {
          email,
        });
      }

      return profile;
    } catch (error) {
      await ServiceProvider.Log.log("Login error", {
        email,
        error: error.message,
      });
      return thunkAPI.rejectWithValue({ error });
    } finally {
      await ServiceProvider.Log.log("Login process completed", { email });
    }
  }
);

export const syncCalendarEventsAsync = createAsyncThunk(
  "login/syncCalendarEvents",
  async (isGoogle) => {
    try {
      await ServiceProvider.Log.log("Syncing calendar events started", {
        isGoogle,
      });

      const response = await ServiceProvider.Calendar.sync(isGoogle);

      await ServiceProvider.Log.log(
        "Syncing calendar events completed successfully",
        { isGoogle, response }
      );
    } catch (error) {
      await ServiceProvider.Log.log("Syncing calendar events failed", {
        isGoogle,
        error,
      });
    }
  }
);

export const getProfileAsync = createAsyncThunk("login/profile", async () => {
  return await ServiceProvider.Auth.getProfile();
});

export const processDemoLoginAsync = createAsyncThunk(
  "login/demoLogin",
  async (_arg, thunkAPI) => {
    try {
      await ServiceProvider.Log.log("Demo login process started");

      const profile = await ServiceProvider.Auth.demoLogin();
      if (!profile) {
        await ServiceProvider.Log.log("Demo login failed: No profile returned");
        return null;
      }

      await ServiceProvider.Log.log("Demo login successful", {
        userId: profile.userId,
      });

      return profile;
    } catch (error) {
      await ServiceProvider.Log.log("Demo login error", {
        error: error.message,
      });
      return thunkAPI.rejectWithValue({ error });
    }
  }
);

export const updateIsCostIndexPresentation = createAsyncThunk(
  "login/updateIsCostIndexPresentation",
  async ({ userId, value }) => {
    await ServiceProvider.User.updateCostIndexPresentation(userId, value);
    return value;
  }
);

export const updateLastInsightsViewed = createAsyncThunk(
  "login/updateLastInsightsViewed",
  async (_arg, thunkAPI) => {
    const state = thunkAPI.getState();
    const userId = state.login.profile.userId;
    return await ServiceProvider.User.updateLastInsightsViewed(userId);
  }
);

function processProfileResponse(state, action) {
  const profileResponse = get(action, "payload", null);

  const { lastViewedInsightsAt, lastInsightsUpdateAt, isDemo, ...restProfile } =
    profileResponse || {};

  state.profile = restProfile;
  state.lastViewedInsightsAt = lastViewedInsightsAt;
  state.lastInsightsUpdateAt = lastInsightsUpdateAt;
  state.isDemo = isDemo || false;
}

export const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setLoginLoading: (state, action) => {
      state.loading = get(action, "payload", false);
    },
    setProfile: (state, action) => {
      state.profile = get(action, "payload", null);
    },
    setLoginError: (state, action) => {
      state.error = get(action, "payload", null);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(processLoginAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(processLoginAsync.fulfilled, (state, action) => {
        processProfileResponse(state, action);
        state.loading = false;
      })
      .addCase(processLoginAsync.rejected, (state, action) => {
        state.profile = null;
        state.loading = false;
        state.error = getError(action.payload);
      })
      .addCase(syncCalendarEventsAsync.pending, (state) => {
        state.syncLoading = true;
      })
      .addCase(syncCalendarEventsAsync.fulfilled, (state) => {
        state.syncLoading = false;
      })
      .addCase(syncCalendarEventsAsync.rejected, (state) => {
        state.syncLoading = false;
      })
      .addCase(updateIsCostIndexPresentation.fulfilled, (state, action) => {
        state.profile.isCostIndexPresentation = get(action, "payload", true);
      })
      .addCase(updateIsCostIndexPresentation.rejected, (state, action) => {
        state.error = getError(action);
      })
      .addCase(getProfileAsync.pending, (state) => {
        state.loadingProfile = true;
      })
      .addCase(getProfileAsync.fulfilled, (state, action) => {
        processProfileResponse(state, action);
        state.loadingProfile = false;
      })
      .addCase(getProfileAsync.rejected, (state) => {
        state.profile = null;
        state.loadingProfile = false;
      })
      .addCase(updateLastInsightsViewed.fulfilled, (state, action) => {
        const data = get(action, "payload", null);
        state.lastViewedInsightsAt = data.lastViewedInsightsAt;
      })
      .addCase(processDemoLoginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processDemoLoginAsync.fulfilled, (state, action) => {
        processProfileResponse(state, action);
        state.loading = false;
      })
      .addCase(processDemoLoginAsync.rejected, (state, action) => {
        state.profile = null;
        state.loading = false;
        state.error = getError(action.payload);
      });
  },
});

export const { setProfile, setLoginError, setLoginLoading } =
  loginSlice.actions;

export const makeProfile = (state) => state.login.profile;
export const makeLastViewedInsightsAt = (state) =>
  state.login.lastViewedInsightsAt;
export const makeLastInsightsUpdateAt = (state) =>
  state.login.lastInsightsUpdateAt;
export const makeLoginError = (state) => state.login.error;
export const makeLoginLoading = (state) => state.login.loading;
export const makeLoginSyncLoading = (state) => state.login.syncLoading;
export const makeProfileLoading = (state) => state.login.loadingProfile;
export const makeIsDemo = (state) => state.login.isDemo;

export default loginSlice.reducer;
